import type { ReactNode } from "react"
import { Box } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faXmark } from "@fortawesome/pro-light-svg-icons"
import { faCircleCheck, faCircleExclamation, faStar } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"

/** The planner's tag styles, one per tag variant used across the week menu. */
export enum PlannerTagVariant {
    /** An allergen carried by the dish; pass `onRemove` to show the clear icon. */
    ALLERGEN = "ALLERGEN",
    /** A row-wide rule, e.g. a price locked for the whole week or a template default. */
    GLOBAL = "GLOBAL",
    /** A marketing tag on a dish, e.g. "Chef's choice". */
    MARKETING = "MARKETING",
    MARKETING_NEW = "MARKETING_NEW",
    MARKETING_NEW_EMPTY = "MARKETING_NEW_EMPTY",
    READY = "READY",
    MISSING = "MISSING",
    OPTIONAL = "OPTIONAL",
    /** Dashed placeholder that adds the thing it names. */
    ADD = "ADD",
    /** Filled/total counters (day header, ready days). */
    COUNT = "COUNT",
}

const useStyles = makeStyles()((theme) => ({
    // The border, background and font resets keep the tag identical when it renders as a button.
    root: {
        display: "inline-flex",
        alignItems: "center",
        gap: ".25rem",
        height: "1.25rem",
        padding: "0 .375rem",
        border: "none",
        borderRadius: ".25rem",
        background: "transparent",
        fontFamily: "inherit",
        fontSize: ".625rem",
        fontWeight: 500,
        whiteSpace: "nowrap",
        "& svg": {
            fontSize: ".5rem",
        },
    },
    // Uppercase status-style tags share the bold label and the tighter left padding.
    status: {
        padding: "0 .375rem 0 .25rem",
        fontWeight: 700,
        textTransform: "uppercase",
    },
    allergen: {
        background: theme.palette.tailwind.amber[200],
        color: theme.palette.tailwind.amber[800],
    },
    global: {
        background: theme.palette.tailwind.blue[50],
        color: theme.palette.tailwind.blue[800],
        fontSize: ".75rem",
        fontWeight: 700,
    },
    marketing: {
        background: theme.palette.tailwind.green[500],
        color: theme.palette.common.white,
        fontSize: ".75rem",
        fontWeight: 400,
    },
    marketingNew: {
        background: theme.palette.tailwind.blue[100],
        color: theme.palette.tailwind.blue[800],
    },
    marketingNewEmpty: {
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[400],
    },
    ready: {
        background: theme.palette.tailwind.green[100],
        color: theme.palette.tailwind.green[600],
    },
    missing: {
        background: theme.palette.tailwind.yellow[100],
        color: theme.palette.tailwind.yellow[700],
    },
    optional: {
        background: theme.palette.tailwind.slate[100],
        color: theme.palette.tailwind.slate[400],
    },
    add: {
        border: `1px dashed ${theme.palette.tailwind.gray[400]}`,
        color: theme.palette.tailwind.slate[600],
    },
    count: {
        justifyContent: "center",
        padding: "0 .5rem",
        borderRadius: ".875rem",
        background: theme.palette.tailwind.amber[100],
        color: theme.palette.tailwind.amber[800],
    },
    clickable: {
        cursor: "pointer",
    },
    removeIcon: {
        display: "flex",
        padding: 0,
        border: "none",
        background: "transparent",
        color: "inherit",
        opacity: 0.6,
        cursor: "pointer",
        "&:hover": {
            opacity: 1,
        },
    },
}))

type Props = Readonly<{
    variant: PlannerTagVariant
    /** The label; wrap the caller's element in LocalizedStrict, this only renders what it is given. */
    children: ReactNode
    onClick?: () => void
    /** Allergen tags only: renders the clear icon. */
    onRemove?: () => void
    className?: string
}>

export function PlannerTag({ variant, children, onClick, onRemove, className }: Props) {
    const { classes, cx } = useStyles()

    const variantClasses: Record<PlannerTagVariant, string> = {
        [PlannerTagVariant.ALLERGEN]: classes.allergen,
        [PlannerTagVariant.GLOBAL]: classes.global,
        [PlannerTagVariant.MARKETING]: classes.marketing,
        [PlannerTagVariant.MARKETING_NEW]: cx(classes.status, classes.marketingNew),
        [PlannerTagVariant.MARKETING_NEW_EMPTY]: cx(classes.status, classes.marketingNewEmpty),
        [PlannerTagVariant.READY]: cx(classes.status, classes.ready),
        [PlannerTagVariant.MISSING]: cx(classes.status, classes.missing),
        [PlannerTagVariant.OPTIONAL]: cx(classes.status, classes.optional),
        [PlannerTagVariant.ADD]: classes.add,
        [PlannerTagVariant.COUNT]: classes.count,
    }

    const leadingIcons: Partial<Record<PlannerTagVariant, ReactNode>> = {
        [PlannerTagVariant.MARKETING_NEW]: <FontAwesomeIcon icon={faStar} />,
        [PlannerTagVariant.MARKETING_NEW_EMPTY]: <FontAwesomeIcon icon={faPlus} />,
        [PlannerTagVariant.READY]: <FontAwesomeIcon icon={faCircleCheck} />,
        [PlannerTagVariant.MISSING]: <FontAwesomeIcon icon={faCircleExclamation} />,
        [PlannerTagVariant.ADD]: <FontAwesomeIcon icon={faPlus} />,
    }

    return (
        // Clickable tags render as real buttons for keyboard access; onClick and onRemove never
        // combine, so the remove icon can be a button inside a plain span without nesting buttons.
        <Box
            component={onClick ? "button" : "span"}
            type={onClick ? "button" : undefined}
            className={cx(classes.root, variantClasses[variant], onClick && classes.clickable, className)}
            onClick={onClick}
        >
            {leadingIcons[variant]}
            {children}
            {onRemove && (
                <Box
                    component="button"
                    type="button"
                    className={classes.removeIcon}
                    onClick={(event) => {
                        event.stopPropagation()
                        onRemove()
                    }}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </Box>
            )}
        </Box>
    )
}
